import { z } from 'zod';
import { VOLL_WALLET_MAX_LIST, VollWalletMoneyInputSchema } from './voll_wallet_money.js';
import { VollWalletAllocationInputSchema } from './voll_wallet_allocation.js';
import { VollWalletRefSchema } from './voll_wallet_pix.js';

/** Uma carteira que aceita pedido de saldo ("por aprovação"): a diária da política, ou o valor
 *  livre quando ela é flexível. */
export const VollWalletBalanceWalletSchema = z
  .object({
    id: z.string().min(1),
    name: z.string(),
    /** Valor por dia da política; numa flexível, o teto informativo (0 = sem teto). */
    dailyRate: z.number().nonnegative(),
    flexible: z.boolean(),
    /** ISO 4217; ausente = real. */
    currency: z.string().length(3).optional(),
  })
  .strict();
export type VollWalletBalanceWallet = z.infer<typeof VollWalletBalanceWalletSchema>;

/**
 * `GET /voll-wallet/balance/form` — o pedido de saldo ("Novo saldo" do app legado): as carteiras
 * por aprovação, a alocação do cadastro (sem ela não há pedido) e os motivos do cliente.
 */
export const VollWalletBalanceFormSchema = z
  .object({
    wallets: z.array(VollWalletBalanceWalletSchema),
    company: VollWalletRefSchema.nullable(),
    costCenter: VollWalletRefSchema.nullable(),
    motives: z.array(VollWalletRefSchema),
  })
  .strict();
export type VollWalletBalanceForm = z.infer<typeof VollWalletBalanceFormSchema>;

const BalancePeriodShape = {
  start: z.iso.date(),
  end: z.iso.date(),
  /** Só as flexíveis levam `amount`: nas demais o BFF calcula diária × dias. Sem carteira
   *  repetida: a recusa é a escolha mais simples (a tela não deixa escolher duas vezes). */
  wallets: z
    .array(
      z.object({ id: z.string().min(1), amount: VollWalletMoneyInputSchema.optional() }).strict(),
    )
    .min(1)
    .max(VOLL_WALLET_MAX_LIST)
    .refine((ws) => new Set(ws.map((w) => w.id)).size === ws.length, {
      message: 'duplicate wallet',
    }),
  /** A alocação escolhida; ausente = a do cadastro. */
  allocation: VollWalletAllocationInputSchema.optional(),
};

const periodInOrder = (r: { start: string; end: string }) => r.start <= r.end;

/** `POST /voll-wallet/balance/budget` — o orçamento do centro de custo para o período e as
 *  carteiras escolhidas, antes de enviar. */
export const VollWalletBalanceBudgetRequestSchema = z
  .object(BalancePeriodShape)
  .strict()
  .refine(periodInOrder, { message: 'start after end' });
export type VollWalletBalanceBudgetRequest = z.infer<typeof VollWalletBalanceBudgetRequestSchema>;

/** `ok` cabe; `partial` há orçamento, mas menos que o pedido; `exceeded` não há orçamento;
 *  `uncontrolled` a carteira não tem orçamento; `unknown` o orçamento não respondeu. */
export const VollWalletBalanceBudgetSchema = z
  .object({
    wallets: z.array(
      z
        .object({
          id: z.string().min(1),
          status: z.enum(['ok', 'partial', 'exceeded', 'uncontrolled', 'unknown']),
          available: z.number().optional(),
        })
        .strict(),
    ),
  })
  .strict();
export type VollWalletBalanceBudget = z.infer<typeof VollWalletBalanceBudgetSchema>;

/** `POST /voll-wallet/balance` — o pedido: a proposta, o período, as carteiras, o motivo e a
 *  alocação (ausente = a do cadastro); o BFF confere as carteiras e a alocação e calcula os
 *  valores. */
export const VollWalletBalanceRequestSchema = z
  .object({
    ...BalancePeriodShape,
    description: z.string().trim().min(1).max(300),
    /** Com o nome: a lista de motivos é paginada, e o BFF confere buscando por ele. */
    motive: VollWalletRefSchema,
    observation: z.string().trim().max(300).optional(),
  })
  .strict()
  .refine(periodInOrder, { message: 'start after end' });
export type VollWalletBalanceRequest = z.infer<typeof VollWalletBalanceRequestSchema>;

export const VollWalletBalanceCreatedSchema = z
  .object({ id: z.string().min(1), total: z.number() })
  .strict();
export type VollWalletBalanceCreated = z.infer<typeof VollWalletBalanceCreatedSchema>;

/** As recusas que a tela explica. `balance_rejected`: o request-flow-api recusou (período em
 *  conflito com outro pedido, crédito ativo na carteira). */
export const VollWalletBalanceErrorSchema = z.discriminatedUnion('error', [
  z.object({ error: z.literal('wallet_unavailable') }).strict(),
  z.object({ error: z.literal('amount_missing') }).strict(),
  z.object({ error: z.literal('period_invalid') }).strict(),
  z.object({ error: z.literal('motive_unavailable') }).strict(),
  z.object({ error: z.literal('allocation_missing') }).strict(),
  /** A empresa ou o centro de custo escolhido não é permitido ao viajante. */
  z.object({ error: z.literal('allocation_invalid') }).strict(),
  z.object({ error: z.literal('balance_rejected') }).strict(),
  /** O termo da política da wallet ainda não foi aceito. */
  z.object({ error: z.literal('policy_pending') }).strict(),
  /** O wallets-api falhou sem recusar: não dá para afirmar que nada foi feito (#5688). */
  z.object({ error: z.literal('outcome_unknown') }).strict(),
]);
export type VollWalletBalanceError = z.infer<typeof VollWalletBalanceErrorSchema>;
